import { html, render } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import _ from 'underscore'
import $ from 'jquery'
import Backbone from '@polleverywhere/backbone'
import { inDOM } from '@polleverywhere/core/lib/util'
import env from '@polleverywhere/core/lib/env'
import BEM from '../../lib/mixins/_bem'
import '@polleverywhere/activity-visualization'
import { proxyActivity } from '@polleverywhere/viz-utils'

// MCPContainer needs this. It should really include it itself
import '../../lib/jquery.toggle_visibility.js'

import './style.scss'

const IFRAME_ATTRIBUTES = {
  allowfullscreen: '',
  frameborder: '0',
  height: '100%',
  mozallowfullscreen: '',
  webkitallowfullscreen: '',
  width: '100%',
  scrolling: 'no',
  title: 'Poll visualization which includes controls to manage the poll state.',
  tabIndex: '-1'
}

function iosDevice () {
  return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)
}

function getVizUrl (model, params = {}) {
  _.defaults(params, {
    url: env.get('api_url').toString(),
    firehose_url: env.get('firehose_url').toString(),
    firehose_ssl: env.get('firehose_ssl').toString(),
    active_tour: false,
    hide: ''
  })

  return env.get('viz_url')
    .segment(model.showUrl())
    .search(params)
}

function createIframeChart (iframeOptions) {
  const options = Object.assign({}, IFRAME_ATTRIBUTES, iframeOptions)
  const $iframe = $('<iframe />').attr(options)

  this.$_toggleModifier(null, this.className, 'loading', true)

  $(window).on(`message.${this.cid}`, (e) => {
    if (e.originalEvent.source === $iframe.get(0).contentWindow) {
      if (e.originalEvent.data === 'finishedLoading') {
        this.$_toggleModifier(null, this.className, 'loading', false)
        this.ready.resolve()
      }
    }
  })

  return $iframe
}

function createChart () {
  return html`
    <activity-visualization
      .model=${this.model}
      .i18n=${this.options.i18n}
      voting-instructions="${ifDefined(this.options.chart.showVotingInstructions)}"
      aspect-ratio="${ifDefined(!this.options.aspectRatio ? undefined : this.options.aspectRatio)}"
    ></activity-visualization>
  `
}

function updateAspectRatio (self) {
  if (!self.options.aspectRatio) return

  const { width } = self.el.getBoundingClientRect()
  const targetHeight = `${(width / self.options.aspectRatio)}px`
  self.el.style.height = targetHeight
  self.el.style.width = width + 'px'
}

/**
 * Renders an iframe or chart component of an activity.
 *
 * Supports locking aspect ratio, resizing, orientation change, and ios safari iframe workarounds.
 */
export default class Visualization extends Backbone.View {
  get className () { return 'component-visualization' }

  /**
   * Constructor
   *
   * @param {Object} [options]
   * @param {Number} [options.aspectRatio=16/9] Aspect ratio to lock to. Use false to turn this off.
   * @param {Boolean} [options.iframeMode] Uses an iframe to render the visualization.
   * @param {Object} [options.iframeQuery] URL params to use when using an iframe.
   * @param {String} [options.iframeSandbox] Sandbox protections. Set to null to omit this attribute. (Default: 'allow-same-origin allow-scripts allow-popups')
   * @param {Object} [options.chart] Options to pass to the Chart components.
   */
  initialize (options = {}) {
    proxyActivity(this.model)
    _.extend(this, BEM(this))

    this.options = _.defaults(options, {
      aspectRatio: 16 / 9,
      iframeMode: true,
      iframeQuery: {
        controls: 'configurator'
      },
      iframeSandbox: 'allow-same-origin allow-scripts allow-popups',
      chart: {}
    })

    if (this.options.chart.modelOverrides) {
      this.model.proxy(this.options.chart.modelOverrides)
    }

    // Chart instance. If in iframeMode, this remains null.
    this.view = null

    // Track whether the viz has loaded or not
    this.ready = new $.Deferred()

    $(window).on(`resize.${this.cid}`, _.throttle(() => {
      // enforce aspect ratio
      requestAnimationFrame(() => {
        updateAspectRatio(this)
      })
    }, 250))
  }

  render () {
    inDOM(this.$el.get(0)).then(() => {
      if (!this.options.iframeMode) {
        render(createChart.call(this), this.el)
        this.view = this.el.querySelector('activity-visualization')
        this.ready.resolve()
      } else {
        this.$el.append(createIframeChart.call(this, {
          src: getVizUrl(this.model, this.options.iframeQuery),
          sandbox: this.options.iframeSandbox
        }))

        // fix ever expanding FTP viz problem specific to IOS devices
        // because they render iframes differently
        if (iosDevice()) {
          $(window).on(`orientationchange.${this.cid}`, () => {
            updateAspectRatio(this)
          })
        }
      }

      updateAspectRatio(this)
    })

    return this
  }

  remove () {
    this.view && this.view.remove()
    $(window).off(`.${this.cid}`)
    super.remove()
  }
}
